import { useCallback, useContext, useState } from 'react'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import type { RecipientIds } from '~/domains/actions/types'
import { toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'

export type SendConfirmationResponse = {
  needsConfirmation: boolean
  invalidApplicants: number[]
  invalidContacts: number[]
}

type UseSendConfirmationFlowOptions = {
  sentToastTitle: string
  sentToastMessage: string
  onFullySent: () => void
  onSendSuccess?: () => void
}

export const useSendConfirmationFlow = ({
  sentToastTitle,
  sentToastMessage,
  onFullySent,
  onSendSuccess
}: UseSendConfirmationFlowOptions) => {
  const [invalidRecipients, setInvalidRecipients] =
    useState<RecipientIds | null>(null)
  const [showInvalidDetail, setShowInvalidDetail] = useState(false)
  const [allRecipientsInvalid, setAllRecipientsInvalid] =
    useState<RecipientIds | null>(null)
  const [skippedAfterSendRecipients, setSkippedAfterSendRecipients] =
    useState<RecipientIds | null>(null)
  const [showSkippedDetail, setShowSkippedDetail] = useState(false)
  const { setIsDirty } = useContext(AsideActionContext)

  const resetBeforeSend = useCallback(() => {
    setInvalidRecipients(null)
    setShowInvalidDetail(false)
    setAllRecipientsInvalid(null)
    setSkippedAfterSendRecipients(null)
    setShowSkippedDetail(false)
  }, [])

  const handleSendSuccess = (response: SendConfirmationResponse) => {
    if (response.needsConfirmation) {
      setInvalidRecipients({
        applicantIds: response.invalidApplicants ?? [],
        contactIds: response.invalidContacts ?? []
      })
      setShowInvalidDetail(false)
      return
    }

    setIsDirty(false)
    onSendSuccess?.()
    if (typeof window.paginatorHistoriqueActions !== 'undefined') {
      window.paginatorHistoriqueActions.refresh()
    }
    toast(sentToastTitle, sentToastMessage, ToastType.Success)

    const skippedApplicantIds = response.invalidApplicants ?? []
    const skippedContactIds = response.invalidContacts ?? []
    if (skippedApplicantIds.length > 0 || skippedContactIds.length > 0) {
      setSkippedAfterSendRecipients({
        applicantIds: skippedApplicantIds,
        contactIds: skippedContactIds
      })
      setShowSkippedDetail(false)
    } else {
      onFullySent()
    }
  }

  return {
    invalidRecipients,
    setInvalidRecipients,
    showInvalidDetail,
    setShowInvalidDetail,
    allRecipientsInvalid,
    setAllRecipientsInvalid,
    skippedAfterSendRecipients,
    showSkippedDetail,
    setShowSkippedDetail,
    resetBeforeSend,
    handleSendSuccess
  }
}
